import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import { ReactComponent as Arrow } from '~/ui-rocco/pages/home-page/images/arrow.svg';
import { getFeaturedWork } from '../utils/featured-work';
import { upscaleImageHandlerUrl } from '../utils/upscale-image';

/**
 * Feature project showcase (Figma §3) + dark "(About)" statement (§4).
 *
 * The lead featured project's image sits on white and overlaps the dark band
 * below — the black background starts ~84% down the image (Figma: image
 * y2198–3318, black bg top y3140), so the image "sticks out" of the dark band.
 * The band then holds the project caption, a leading arrow + indented "(About)"
 * eyebrow nested into the two-tone statement, and a View-all-Projects CTA. The
 * band carries straight into the Selected-work section (also dark).
 */
export const ProfessionalAboutSection = () => {
    const { professionalOverview, featuredProjects, associatedProjects } =
        useLoaderData<typeof loader>();

    // Product suppliers (e.g. Resene) have no projects of their own — fall back
    // to associated projects so the feature image isn't empty.
    const lead = getFeaturedWork(
        featuredProjects.featuredProjectsList,
        associatedProjects,
    )[0];
    const projectsLink = `${professionalOverview.Link}/case-studies`;

    return (
        // Desktop-only: the editorial "(About)" statement + lead feature image is
        // a desktop magazine device. On mobile the Selected-work grid below leads
        // instead, so the whole band is hidden rather than squeezed.
        <section className="relative w-full max-md:hidden">
            {/* Feature image — on white, overlapping the dark band (kept on top). */}
            {lead ? (
                <div className="relative z-10 mx-auto w-full max-w-[120rem] md:px-[3rem]">
                    <RoccoLink
                        to={lead.link}
                        className="bg-neutral-900 relative block w-full overflow-hidden max-md:aspect-[414/300] md:aspect-[1820/1102]"
                    >
                        <img
                            src={upscaleImageHandlerUrl(lead.images?.[0]?.src)}
                            alt={lead.images?.[0]?.alt ?? lead.title}
                            className="absolute inset-0 size-full object-cover"
                            loading="lazy"
                        />
                    </RoccoLink>
                </div>
            ) : null}

            {/* Dark band — when there's a feature image, slid up behind its
                lower ~16% so the image sticks out, with pt dropping the caption
                just below it. With no image (e.g. product suppliers with no
                featured projects) drop that geometry so the band doesn't open
                on a tall empty void. */}
            <div
                className={cn(
                    'bg-[#181818] text-white max-md:pb-12 md:pb-[2.4rem]',
                    lead
                        ? 'md:-mt-[13.1rem] md:pt-[16.225rem]'
                        : 'max-md:pt-2 md:pt-[3.6rem]',
                )}
            >
                {lead ? (
                    <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-4 md:pl-[2.3124rem]">
                        <p className="m-0 font-sans leading-tight font-normal text-white max-md:text-base md:text-[1.248rem]">
                            {lead.title}
                        </p>
                        {lead.professional?.name ? (
                            <p className="m-0 font-sans leading-tight text-white/50 max-md:text-sm md:text-[1.08rem]">
                                {lead.professional.name}
                            </p>
                        ) : null}
                    </div>
                ) : null}

                {/* (About) statement — mirrors the homepage IntroSection: leading
                    arrow far left, statement + CTA indented to 25.9% (Figma:
                    arrow x97.87, statement x497.59). */}
                <div className="relative mx-auto w-full max-w-[120rem] max-md:px-6 max-md:py-12 md:pt-[9.375rem] md:pb-0">
                    {/* Leading arrow — the homepage's filled arrow glyph,
                        recoloured white for the dark band. */}
                    <Arrow
                        aria-hidden="true"
                        className="absolute hidden md:top-[10.375rem] md:left-[5.1%] md:block md:w-[3.6rem] [&_path]:fill-white"
                    />
                    <div className="md:relative md:mr-[3%] md:ml-[25.9%] md:max-w-[68%]">
                        <p className="relative m-0 font-sans leading-[1.15] font-normal text-white max-md:text-[1.625rem] md:[text-indent:22%] md:text-[5rem] md:leading-[1.07] md:tracking-[-0.24rem]">
                            {/* (About) nests into the first-line indent (absolute
                                on desktop, inline on mobile). */}
                            <span className="font-sans text-white max-md:mr-2 max-md:align-top max-md:text-sm md:absolute md:top-[0.9rem] md:left-0 md:[text-indent:0] md:text-[1.75rem] md:tracking-[-0.076875rem]">
                                (About)
                            </span>
                            <span>
                                {professionalOverview.Title} is where great
                                design gets discovered and decided
                            </span>
                            <span className="text-white/40">
                                {' '}
                                — connecting professionals and homeowners.
                            </span>
                        </p>
                        <RoccoLink
                            to={projectsLink}
                            className={detailCta(
                                'grey-dark',
                                // 95px (responsive) below the statement, nudged
                                // 20px right of its column.
                                'w-fit max-md:mt-6 md:ml-[1.25rem] md:mt-[5.9375rem]',
                            )}
                        >
                            View all Projects
                        </RoccoLink>
                    </div>
                </div>
            </div>
        </section>
    );
};
